import React, { useState, useEffect } from 'react';
import { X, User, IndianRupee, Save, Loader2, MessageSquare, Landmark, Wallet, Calendar, Clock } from 'lucide-react';
import styles from './AdvanceRequests.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const AdvanceRequestDrawer = ({ isOpen, onClose, onSave, record = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    request_type: 'advance', // 'advance' or 'loan'
    recovery_months: '',
    loan_term_type: 'monthly', // 'monthly' or 'yearly'
    loan_duration: '',
    requested_amount: '',
    remark: ''
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);

  const fetchEmployees = async () => {
    setFetchingEmployees(true);
    try {
      const response = await api.get('/employees-dropdown');
      if (response.data.success) {
        setEmployees(response.data.data.map(e => ({
          value: e.id,
          label: `${e.employee_name} (${e.designation_name})`
        })));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setFetchingEmployees(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  useEffect(() => {
    if (record && isOpen) {
      setFormData({
        employee_id: record.employee_id || '',
        request_type: record.request_type || 'advance',
        recovery_months: record.recovery_months || '',
        loan_term_type: record.loan_term_type || 'monthly',
        loan_duration: record.loan_duration || '',
        requested_amount: record.requested_amount || '',
        remark: record.request_remark || record.remark || ''
      });
    } else if (!record) {
      setFormData({
        employee_id: '',
        request_type: 'advance',
        recovery_months: '',
        loan_term_type: 'monthly',
        loan_duration: '',
        requested_amount: '',
        remark: ''
      });
    }
  }, [isOpen, record]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.requested_amount) {
      toast.error('Please fill required fields');
      return;
    }

    setLoading(true);
    try {
      if (record) {
        // Update - PUT /api/advance-requests/{id} (JSON)
        await api.put(`/advance-requests/${record.id}`, {
          requested_amount: formData.requested_amount,
          request_remark: formData.remark
        });
        toast.success('Advance request updated');
      } else {
        // Create - POST /api/advance-requests (Multipart although we don't have files here now)
        const fd = new FormData();
        fd.append('employee_id', formData.employee_id);
        fd.append('request_date', new Date().toLocaleDateString('en-CA'));
        fd.append('requested_amount', formData.requested_amount);
        fd.append('request_remark', formData.remark);

        // Include other fields just in case backend supports them
        fd.append('request_type', formData.request_type);
        if (formData.request_type === 'advance') fd.append('recovery_months', formData.recovery_months);
        if (formData.request_type === 'loan') {
          fd.append('loan_term_type', formData.loan_term_type);
          fd.append('loan_duration', formData.loan_duration);
        }

        await api.post('/advance-requests', fd);
        toast.success('Advance request created');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving advance request:', error);
      toast.error(error.response?.data?.message || 'Failed to save request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
              {record ? 'Edit Advance Request' : 'New Advance Request'}
            </h2>
            <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>Manage salary advance details.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            <div className={styles['form-group']}>
              <label><User size={16} /> Employee</label>
              <CustomSelect
                options={employees}
                value={employees.find(e => e.value === formData.employee_id)}
                onChange={(opt) => setFormData({ ...formData, employee_id: opt.value })}
                placeholder="Select Employee"
                isLoading={fetchingEmployees}
                isDisabled={!!record}
              />
            </div>

            <div className={styles['form-group']}>
              <label><Calendar size={16} /> Recovery Months</label>
              <input
                type="number"
                className={styles['form-input']}
                placeholder="e.g. 3 months"
                value={formData.recovery_months}
                onChange={(e) => setFormData({ ...formData, recovery_months: e.target.value })}
              />
            </div>

            <div className={styles['form-group']}>
              <label><IndianRupee size={16} /> Requested Amount</label>
              <input
                type="number"
                className={styles['form-input']}
                placeholder="Enter amount"
                value={formData.requested_amount}
                onChange={(e) => setFormData({ ...formData, requested_amount: e.target.value })}
              />
            </div>

            <div className={styles['form-group']}>
              <label><MessageSquare size={16} /> Remark</label>
              <textarea
                className={styles['form-input']}
                style={{ minHeight: '100px', resize: 'vertical' }}
                placeholder="Optional notes..."
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
              <span style={{ marginLeft: '8px' }}>{record ? 'Update' : 'Submit'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvanceRequestDrawer;
